import {
	ArrowForward as ArrowForwardIcon,
	Info as InfoIcon,
	LocalOffer as OfferIcon,
	School as SchoolIcon,
	AccessTime as TimeIcon
} from '@mui/icons-material';
import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Container,
	Grid,
	Stack,
	Typography
} from '@mui/material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { NavLink, useLocation } from 'react-router-dom';

const CoursesList = () => {
	const { enqueueSnackbar } = useSnackbar();
	const [courses, setCourses] = useState([]);
	const [loading, setLoading] = useState(true);
	const apiURL = process.env.REACT_APP_API;

	// Color palette for course cards
	const colorPalette = [
		{ bgColor: '#e0e7ff', iconColor: '#4f46e5' }, // Light indigo
		{ bgColor: '#d1fae5', iconColor: '#059669' }, // Light emerald
		{ bgColor: '#fee2e2', iconColor: '#dc2626' }, // Light red
		{ bgColor: '#f3e8ff', iconColor: '#7c3aed' }, // Light violet
		{ bgColor: '#cffafe', iconColor: '#0891b2' }, // Light cyan
		{ bgColor: '#ffedd5', iconColor: '#ea580c' }  // Light orange
	];

	// Capture referral code from URL and store in localStorage
	const location = useLocation();
	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const referral = params.get('referral');
		if (referral) {
			localStorage.setItem('referral_code', referral);
		}
	}, [location.search]);

	// Fetch courses from API
	useEffect(() => {
		fetchCourses();
	}, []);

	const fetchCourses = async () => {
		setLoading(true);
		try {
			const response = await axios.get(`${apiURL}/public/course/getall`);

			if (response.data.status === 'success' || response.data.status === true) {
				const coursesData = response.data.data || [];
				// Add colors to each course
				const coursesWithColors = coursesData.map((course, index) => ({
					...course,
					...colorPalette[index % colorPalette.length]
				}));
				setCourses(coursesWithColors);
			} else {
				toast.error(response.data.message || 'Failed to fetch courses');
			}
		} catch (error) {
			console.error('Error fetching courses:', error);
			toast.error(error.response?.data?.message || 'An error occurred while fetching courses');
		} finally {
			setLoading(false);
		}
	};

	const handleKnowMore = (course) => {
		enqueueSnackbar(`Details for ${course.c_title}`, { variant: 'info' });
	};

	const handleJoinCourse = (course) => {
		enqueueSnackbar(`Joined ${course.c_title} successfully!`, { variant: 'success' });
	};

	const formatCurrency = (amount) => {
		return new Intl.NumberFormat('en-IN', {
			style: 'currency',
			currency: 'INR',
			maximumFractionDigits: 0
		}).format(amount);
	};

	const calculateDiscount = (maxPrice, price) => {
		return Math.round(((maxPrice - price) / maxPrice) * 100);
	};

	return (
		<Box sx={{ py: 4 }}>
			<Container maxWidth="lg">
				{/* Page Header */}
				<Box sx={{ mb: 4, textAlign: 'center' }}>
					<Typography variant="h4" fontWeight={600} gutterBottom>
						Explore Our Courses
					</Typography>
					<Typography variant="body1" color="text.secondary">
						Learn from industry experts and advance your career
					</Typography>
				</Box>

				{/* Loading State */}
				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
						<Stack spacing={2} alignItems="center">
							<CircularProgress size={60} />
							<Typography variant="h6" color="text.secondary">
								Loading courses...
							</Typography>
						</Stack>
					</Box>
				) : courses.length > 0 ? (
					<>
						{/* Courses Grid */}
						<Grid container spacing={3}>
							{courses.map((course) => {
								const discount = calculateDiscount(course.c_max_price, course.c_price);

								return (
									<Grid item xs={12} sm={6} md={4} key={course.courseid}>
										<Card
											sx={{
												height: '100%',
												display: 'flex',
												flexDirection: 'column',
												borderRadius: 2,
												border: '1px solid',
												borderColor: 'divider',
												transition: '0.2s',
												'&:hover': {
													boxShadow: 3
												}
											}}
										>
											<CardContent sx={{ flexGrow: 1, p: 3 }}>
												{/* Course Title with Icon */}
												<Stack direction="row" alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
													<Box
														sx={{
															bgcolor: course.bgColor,
															borderRadius: 1.5,
															p: 1.5,
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															minWidth: 56,
															minHeight: 56
														}}
													>
														<SchoolIcon sx={{ color: course.iconColor, fontSize: 28 }} />
													</Box>
													<Box sx={{ flex: 1 }}>
														<Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.2 }}>
															{course.c_title}
														</Typography>
														<Typography variant="caption" color="text.secondary">
															{course.c_name}
														</Typography>
													</Box>
												</Stack>

												{/* Description */}
												<Typography
													variant="body2"
													color="text.secondary"
													sx={{
														mb: 3,
														lineHeight: 1.5,
														display: '-webkit-box',
														WebkitLineClamp: 2,
														WebkitBoxOrient: 'vertical',
														overflow: 'hidden',
														textOverflow: 'ellipsis'
													}}
												>
													{course.c_description}
												</Typography>

												{/* Duration */}
												<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
													<TimeIcon fontSize="small" color="action" />
													<Typography variant="body2">
														Duration: <strong>{course.c_duration} months</strong>
													</Typography>
												</Stack>

												{/* Pricing Section with Right-aligned Offer */}
												<Box sx={{ mb: 3 }}>
													<Stack direction="row" justifyContent="space-between" alignItems="center">
														<Stack direction="row" alignItems="baseline" spacing={1}>
															<Typography variant="h5" fontWeight={700} color="primary.main">
																{formatCurrency(course.c_price)}
															</Typography>
															{course.c_price < course.c_max_price && (
																<Typography
																	variant="body2"
																	color="text.secondary"
																	sx={{ textDecoration: 'line-through' }}
																>
																	{formatCurrency(course.c_max_price)}
																</Typography>
															)}
														</Stack>

														{/* Discount Chip aligned to right */}
														{discount > 0 && (
															<Chip
																label={`${discount}% OFF`}
																size="small"
																color="success"
																icon={<OfferIcon />}
																sx={{ fontWeight: 500, color: 'white' }}
															/>
														)}
													</Stack>
												</Box>

												{/* Action Buttons */}
												<Stack direction="row" spacing={1.5}>
													<NavLink to={`/courses/details?courseid=${course.courseid}`} style={{ textDecoration: 'none', width: '100%' }} >
														<Button
															fullWidth
															variant="outlined"
															startIcon={<InfoIcon />}
															sx={{ borderRadius: 1 }}
														>
															Know More
														</Button>
													</NavLink>
													<NavLink to={`/courses/enquiry?courseid=${course.courseid}`} style={{ textDecoration: 'none', width: '100%' }} >
														<Button
															fullWidth
															variant="contained"
															endIcon={<ArrowForwardIcon />}
															sx={{ borderRadius: 1 }}
														>
															Join Course
														</Button>
													</NavLink>
												</Stack>
											</CardContent>
										</Card>
									</Grid>
								);
							})}
						</Grid>

						{/* Footer Note */}
						<Box sx={{ mt: 6, textAlign: 'center' }}>
							<Typography variant="body2" color="text.secondary">
								{courses.length} courses available • Certificate included with every course
							</Typography>
						</Box>
					</>
				) : (
					/* No Courses State */
					<Box sx={{ textAlign: 'center', py: 8 }}>
						<SchoolIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 3, opacity: 0.5 }} />
						<Typography variant="h5" color="text.secondary" gutterBottom fontWeight={600}>
							No courses available
						</Typography>
						<Typography variant="body1" color="text.secondary">
							Please check back later for new courses
						</Typography>
					</Box>
				)}
			</Container>
		</Box>
	);
};

export default CoursesList;